<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import lottieFinanzjournalismus from '../icons/lottie-finanzjournalismus.json';
import lottieZielzonen from '../icons/lottie-praezise-zielzonen.json';
import lottie from 'lottie-web';
import { asset } from '../../helpers';
import { store } from '../../store.js';

// define props using Composition API
const props = defineProps({
    name: String,
    preview: Boolean,
    authors: Array
});

// fallback image handler
const fallbackImage = (params) => {
    params.target.src = asset('/hkcm/images/logo-small-on-dark-background.svg', 'public');
};

// local reactive state
const info = ref(null);
const authors = ref([]);
const loading = ref(false);

// get matching author info
const updateAuthorInfo = () => {
    info.value = authors.value.find((author) => author.author === props.name);
};

// compute unique ID extension
const getIdExtension = () => props.name.replace(' ', '-').toLowerCase() + '-icon';

const initLottieIcons = () => {
    const setup = (doc_id, lottie_data, delay = 0) => {
        const iconAnimation = lottie.loadAnimation({
            container: document.getElementById(doc_id),
            animationData: lottie_data,
            renderer: 'svg',
            loop: false,
            autoplay: true
        });

        iconAnimation.addEventListener('complete', () => {
            setTimeout(() => {
                iconAnimation.goToAndPlay(0, true);
            }, delay);
        });
    };

    setup('core-topic-' + getIdExtension(), lottieZielzonen, 5000);
    setup('publications-' + getIdExtension(), lottieFinanzjournalismus, 5200);
};

const fetchAuthors = async () => {
    loading.value = true;
    try {
        await store.dispatch('authors/index');
        authors.value = store.getters['authors/all'];
    } catch (error) {
        console.error('Error loading team data:', error);
    } finally {
        loading.value = false;
    }
};

const loadAuthors = async () => {
    if (Array.isArray(props.authors) && props.authors.length > 0) {
        authors.value = props.authors;
    } else {
        await fetchAuthors();
    }
};

onMounted(async () => {
    await loadAuthors();
    updateAuthorInfo();

    if (info.value) {
        await nextTick();
        initLottieIcons();
    }
});

// watch for prop changes
watch(
    () => props.name,
    () => {
        updateAuthorInfo();
    }
);
</script>

<template>
    <div v-if="info">
        <!-- Small version -->
        <div
            v-if="props.preview"
            class="group/author-preview max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl hover:shadow-xl transition-all ease-in-out duration-300"
        >
            <router-link :to="'/hkcmnews/archive/?author=' + info.id + '&author_name=' + info.author">
                <div class="md:flex">
                    <div class="md:shrink-0">
                        <img class="h-48 w-full object-cover md:h-full md:w-48" :src="info.portrait" :alt="info.author" />
                    </div>
                    <div class="p-6">
                        <div class="uppercase tracking-wide text-sm text-blue font-semibold group-hover/author-preview:underline">
                            <div>{{ $t('more_articles_from') }}: {{ info.author }}</div>
                        </div>
                        <div class="mt-6">
                            <!-- Metriken -->
                            <div class="flex flex-wrap items-center gap-3">
                                <div class="flex gap-3 items-center">
                                    <div :id="'publications-' + getIdExtension()" class="w-10 h-10"></div>
                                    <div class="text-blue-dark">
                                        <div class="text-lg font-semibold">{{ info.publishedArticles }}</div>
                                        <div class="text-xxs break-words">{{ $t('published_articles') }}</div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="info.coreTopics.length > 0" class="mt-6">
                                <div class="flex items-center mb-3">
                                    <div :id="'core-topic-' + getIdExtension()" class="w-10 h-10"></div>
                                    <div class="text-blue-dark">{{ $t('core_topics') }}</div>
                                </div>

                                <div class="flex flex-wrap items-center gap-3">
                                    <div v-for="topic in info.coreTopics" class="rounded-full bg-blue-light flex items-center p-3 py-2 gap-2">
                                        <span class="bg-blue w-3 h-3 rounded-full block"></span>
                                        <span class="uppercase text-xxs">{{ topic }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </router-link>
        </div>

        <!-- Big version -->
        <div v-else-if="info.vita" class="container rounded-md bg-white my-6 py-6 shadow-md">
            <h4 class="mb-3">{{ $t('author_profile') }}: {{ info.author }}</h4>
            <div aria-label="author-data">
                <div class="max-w-5xl mx-auto bg-white p-6 xl:p-10 rounded-xl shadow-lg grid grid-cols-1 xl:grid-cols-2 gap-8 items-center">
                    <!-- Portrait -->
                    <div class="flex justify-center">
                        <div class="aspect-[3/4] max-h-[500px] w-full max-w-sm sm:max-w-md overflow-hidden rounded-xl shadow-md">
                            <img :src="info.portrait" :alt="$t('portrait_of', { name: info.author })" class="w-full h-full object-cover" />
                        </div>
                    </div>
                    <!-- Vita -->
                    <div>
                        <p class="text-blue-dark text-justify leading-relaxed text-base space-y-4" v-html="$translate(info.vita)"></p>
                    </div>
                </div>

                <div class="h-[2px] w-full bg-blue-light my-6"></div>

                <div>
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                        <!-- Lieblingsprodukte -->
                        <div v-if="info.favProducts.length > 0" class="bg-white p-4 rounded-lg shadow-md">
                            <h3 class="text-sm font-semibold mb-3">{{ $t('favorite_products') }}:</h3>
                            <div class="flex flex-wrap gap-3">
                                <a v-for="product in info.favProducts" href="/produkte" class="block">
                                    <div v-if="product.image !== null">
                                        <img :src="product.image" class="w-20 h-20 object-cover rounded-md border" />
                                    </div>
                                    <div v-else class="w-16 h-16 flex items-center justify-center opacity-20 border rounded-md">
                                        <Loading :loading="true" />
                                    </div>
                                </a>
                            </div>
                        </div>

                        <!-- Lieblingsartikel -->
                        <div v-if="info.favPost" class="bg-white p-4 rounded-lg shadow-md">
                            <h3 class="text-sm font-semibold mb-3">{{ $t('favorite_article') }}:</h3>
                            <div v-if="info.favPost.slug" class="flex gap-3 items-start">
                                <img class="h-16 w-16 object-cover rounded-md border" :src="info.favPost?.image" alt="" @error="fallbackImage" />
                                <div class="text-xs space-y-1">
                                    <div class="text-gray-500">
                                        {{ new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: '2-digit', year: 'numeric' }).format(new Date(info.favPost?.published_at)) }}
                                    </div>
                                    <div class="font-semibold text-blue-dark">{{ $translate(info.favPost?.title) }}</div>
                                    <a :href="'https://hkcmanagement.de/hkcmnews/' + info.favPost?.slug" class="text-blue hover:underline">
                                        {{ $t('read_more') }}
                                    </a>
                                </div>
                            </div>
                            <div v-else class="h-16 flex items-center justify-center opacity-20 border rounded-md">
                                <Loading :loading="true" />
                            </div>
                        </div>

                        <!-- Kernthemen -->
                        <div v-if="info.coreTopics.length > 0" class="bg-white p-4 rounded-lg shadow-md">
                            <h3 class="text-sm font-semibold mb-3">{{ $t('core_topics') }}:</h3>
                            <div class="flex flex-wrap gap-2">
                                <div v-for="topic in info.coreTopics" class="flex items-center gap-2 px-3 py-1 bg-blue-light text-blue-dark text-xxs rounded-full">
                                    <span class="w-2 h-2 bg-blue rounded-full"></span>
                                    <span class="uppercase">{{ topic }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Veröffentlichungen -->
                        <div class="bg-white p-4 rounded-lg shadow-md">
                            <h3 class="text-sm font-semibold mb-3">{{ $t('published_articles') }}:</h3>
                            <div class="flex gap-3 items-center">
                                <div :id="'publications-' + getIdExtension()" class="w-16 h-16"></div>
                                <div class="text-blue">
                                    <div class="text-lg font-semibold">{{ info.publishedArticles }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
